import React, { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon } from 'lucide-react';
import { usePagination } from 'react-instantsearch';
import DownArrow from '@/components/customercenter-ui/svgs/arrowDown';
import LeftArrow from '@/components/customercenter-ui/svgs/chevronLeft';
import RightArrow from '@/components/customercenter-ui/svgs/chevronRight_Arrow';
import { TranslateText } from '@/helpers/utils/siteUtils';
import { DEFAULT_SITE_LANG } from '../../../../../../constants/constant';
import { PageTexts } from '../i18n';

const PAGE_BUFFER = 40;

export const CustomPagination = () => {
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const searchQuery = useSearchParams().get('q') ?? '';
  const { pages, currentRefinement, isFirstPage, isLastPage, refine, createURL, nbHits } = usePagination({
    totalPages: searchQuery ? 200 : 20,
    padding: searchQuery ? 200 : 20,
  });
  const [visiblePages, setVisiblePages] = useState(pages.slice(0, PAGE_BUFFER));
  const startOrder = (currentRefinement + 1 - 1) * 6 + 1;
  const endOrder = Math.min(startOrder + 6 - 1, nbHits);

  useEffect(() => {
    setVisiblePages(
      pages.slice(
        Math.max(currentRefinement - PAGE_BUFFER / 2, 0),
        Math.min(currentRefinement + PAGE_BUFFER / 2, pages.length),
      ),
    );
  }, [currentRefinement, pages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 500);
    return () => clearTimeout(timer);
  }, [currentRefinement]);

  const handlePageChange = (selectedPage: number) => {
    refine(selectedPage);
  };

  if (pages.length < 2) {
    return null;
  }

  return (
    <section className="flex flex-col items-center">
      <section className="relative flex w-[200px] justify-center gap-8 px-5">
        <PaginationItem
          isDisabled={isFirstPage}
          href={createURL(currentRefinement - 1)}
          onClick={() => refine(currentRefinement - 1)}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white drop-shadow-lg">
            <LeftArrow className="h-4 w-4 fill-black" />
          </span>
        </PaginationItem>
        <Listbox value={currentRefinement} onChange={handlePageChange}>
          <div className=" mt-1">
            <ListboxButton className="relative w-full cursor-default  bg-white  pr-6 text-left  focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
              <span className="block truncate text-sm leading-[19.6px] text-black">
                {Texts.page} {currentRefinement + 1} of {pages.length}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pl-2">
                <DownArrow className="h-5 w-5" aria-hidden="true" />
              </span>
            </ListboxButton>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="lg absolute right-0 top-10 z-10  mt-1  w-full rounded-md bg-white text-base ring-1 ring-black/5 focus:outline-none sm:text-sm">
                <section className="relative  rounded-md border border-foggyGray  bg-white py-3 shadow-md">
                  <div className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-l border-t border-foggyGray bg-white"></div>
                  <section className="custom-scrollbar h-[100px] divide-y divide-foggyGray overflow-y-auto ">
                    {visiblePages.map((page) => (
                      <ListboxOption
                        key={page}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-4 pr-5 text-sm leading-[19.6px] text-black ${
                            active ? 'foggyGray text-black' : 'text-black'
                          }`
                        }
                        value={page}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate text-sm leading-[19.6px] text-black ${
                                selected ? 'font-medium' : 'font-normal'
                              }`}
                            >
                              {Texts.page} {page + 1}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pl-3 text-black">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </section>
                </section>
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
        {!isLastPage && (
          <PaginationItem
            isDisabled={isLastPage}
            href={createURL(currentRefinement + 1)}
            onClick={() => refine(currentRefinement + 1)}
          >
            <span className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white drop-shadow-lg">
              <RightArrow className="h-4 w-4 fill-black" />
            </span>
          </PaginationItem>
        )}
      </section>
      <div className="mt-4">
        Displaying {startOrder}-{endOrder} of {nbHits} Items
      </div>
    </section>
  );
};

function PaginationItem({ isDisabled, onClick, ...props }: any) {
  return (
    <button
      className="cursor-pointer"
      onClick={(event) => {
        event.preventDefault();
        onClick(event);
      }}
      disabled={isDisabled}
      {...props}
    >
      {props.children}
    </button>
  );
}
